"use client";

import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Madeinusa() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const chapters = [{
    year: "2005",
    megaYear: "'05",
    coord: "§ 02 / 2026 / THE FOUNDATION",
    title: "PRECISION BORN IN THE HEARTLAND",
    body: "It started with a single CNC machine in a small garage, a commitment to American craftsmanship, and a vision to provide industrial-grade identification solutions that actually last.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/timeline_2005_founding.webp",
    caption: "Our first workshop, 2005",
    stickerText: "ESTABLISHED",
    stickerColor: "bg-primary",
    stickerRotation: "-rotate-6",
    reverse: false
  }, {
    year: "2012",
    megaYear: "'12",
    coord: "§ 03 / 2012 / SCALING UP",
    title: "EXPANDING OUR CAPABILITIES",
    body: "As demand for high-quality electrical labels grew, we upgraded our facility and added advanced laser engraving technology to serve the complex needs of panel builders and engineers.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/timeline_2012_expansion.png",
    caption: "Upgrading our production line",
    stickerText: "TECH UPGRADE",
    stickerColor: "bg-primary",
    stickerRotation: "rotate-3",
    reverse: true
  }, {
    year: "2018",
    megaYear: "'18",
    coord: "§ 04 / 2018 / INDUSTRY LEADERS",
    title: "SETTING THE STANDARD",
    body: "We became the go-to partner for critical infrastructure projects, delivering submittal packages and custom identification that meet the most rigorous UL and industrial standards.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/timeline_2018_industry.png",
    caption: "Serving major infrastructure",
    stickerText: "UL CERTIFIED",
    stickerColor: "bg-primary",
    stickerRotation: "-rotate-3",
    reverse: false
  }, {
    year: "2024",
    megaYear: "'24",
    coord: "§ 05 / 2024 / FUTURE READY",
    title: "A DECADE OF EXCELLENCE",
    body: "Today, we continue to push the boundaries of precision marking, serving industries from aerospace to power generation with the same dedication to quality we started with.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/timeline_2024_modern.png",
    caption: "Our team today",
    stickerText: "MADE IN USA",
    stickerColor: "bg-primary",
    stickerRotation: "rotate-6",
    reverse: true
  }];
  return;
}
function ChapterBlock({
  chapter,
  index,
  isLast
}: {
  chapter: {
    year: string;
    megaYear: string;
    coord: string;
    title: string;
    body: string;
    image: string;
    caption: string;
    stickerText: string;
    stickerColor: string;
    stickerRotation: string;
    reverse: boolean;
  };
  index: number;
  isLast: boolean;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const polaroidRotations = ["-rotate-3", "rotate-2", "-rotate-2", "rotate-3"];
  const polaroidRotation = polaroidRotations[index % polaroidRotations.length];
  return <div ref={ref} data-index={index} className="relative pb-24 md:pb-32">
      {}
      <div aria-hidden="true" className={`pointer-events-none absolute top-1/2 -translate-y-1/2 select-none font-serif font-default text-muted ${chapter.reverse ? "right-0 md:right-8" : "left-0 md:left-8"}`} style={{
      fontSize: "clamp(8rem, 22vw, 22rem)",
      lineHeight: "0.85",
      opacity: 0.15,
      zIndex: 0,
      letterSpacing: "-0.04em"
    }}>
        {chapter.megaYear}
      </div>

      {}
      <div className={`relative z-10 grid grid-cols-1 items-center gap-10 md:gap-16 lg:grid-cols-12 ${chapter.reverse ? "" : ""}`}>
        {}
        <div className={`${chapter.reverse ? "lg:col-span-5 lg:order-2" : "lg:col-span-7 lg:order-1"} flex justify-center lg:justify-start ${chapter.reverse ? "lg:justify-end" : ""}`}>
          <div className={`relative transition-all duration-700 ease-out ${polaroidRotation} ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
          transitionDelay: "100ms"
        }}>
            {}
            <div aria-hidden="true" className="absolute -top-5 left-6 z-20 h-8 w-24 -rotate-12 border border-foreground/20 bg-primary/70" />
            {}
            <div aria-hidden="true" className="absolute -top-4 right-8 z-20 h-7 w-20 rotate-6 border border-foreground/20 bg-primary/70" />

            {}
            <div className="relative bg-card p-3 pb-12 md:p-4 md:pb-16 border border-border" style={{
            boxShadow: "8px 8px 0 0 #000000",
            borderRadius: "0.5rem"
          }}>
              <div className="overflow-hidden">
                <img src={chapter.image} data-wvc-srcset={`${chapter.image}-wvc-srcset`} data-wvc-sizes={`${chapter.image}-wvc-sizes`} alt={chapter.caption} loading="lazy" decoding="async" className="aspect-[4/5] w-[280px] object-cover md:w-[360px] lg:w-[420px]" />
              </div>
              <p className="font-serif font-default mt-4 text-center text-card-foreground text-lg md:text-xl">
                {chapter.caption}
              </p>

              {}
              <Badge className={`absolute -right-6 -top-6 z-30 ${chapter.stickerColor} ${chapter.stickerRotation} border-2 border-background px-3 py-2 font-default text-xs font-bold uppercase text-primary-foreground`} style={{
              boxShadow: "3px 3px 0 0 #000000",
              borderRadius: "0.5rem",
              letterSpacing: "0.04em"
            }}>
                {chapter.stickerText}
              </Badge>
            </div>
          </div>
        </div>

        {}
        <div className={`${chapter.reverse ? "lg:col-span-7 lg:order-1" : "lg:col-span-5 lg:order-2"} transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
        transitionDelay: "200ms"
      }}>
          <p className="font-mono text-xs uppercase text-primary md:text-sm" style={{
          letterSpacing: "0.12em",
          fontWeight: 500
        }}>
            {chapter.coord}
          </p>
          <h3 className="font-serif font-default mt-4 text-foreground" style={{
          fontSize: "clamp(1.75rem, 3.2vw, 2.75rem)",
          lineHeight: "1.02",
          letterSpacing: "-0.01em"
        }}>
            {chapter.title}
          </h3>
          <p className="font-default mt-5 text-foreground/85" style={{
          fontSize: "clamp(1rem, 1.1vw, 1.0625rem)",
          lineHeight: "1.6"
        }}>
            {chapter.body}
          </p>

          {}
          <div className="mt-8 flex items-center gap-0">
            <div className="h-[2px] w-32 bg-foreground" />
            <div className="h-3 w-3 rounded-lg bg-primary" />
          </div>
        </div>
      </div>

      {}
      {!isLast && <div aria-hidden="true" className="absolute left-1/2 top-full z-10 hidden h-24 w-12 -translate-x-1/2 md:block">
          <svg viewBox="0 0 48 96" fill="none" className="h-full w-full" preserveAspectRatio="none">
            <path d="M 24 0 Q 8 16 24 32 Q 40 48 24 64 Q 8 80 24 92" stroke="#A1A1AA" strokeWidth="3" strokeLinecap="round" fill="none" className="transition-[stroke-dashoffset] duration-[1000ms] ease-out" style={{
          strokeDasharray: 200,
          strokeDashoffset: isVisible ? 0 : 200
        }} />
            {}
            <path d="M 18 86 L 24 94 L 30 86" stroke="#A1A1AA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" className="transition-opacity duration-500" style={{
          opacity: isVisible ? 1 : 0,
          transitionDelay: "800ms"
        }} />
          </svg>
        </div>}

      {}
      {!isLast && <div aria-hidden="true" className="absolute left-6 top-full z-10 block h-16 w-8 md:hidden">
          <svg viewBox="0 0 32 64" fill="none" className="h-full w-full" preserveAspectRatio="none">
            <path d="M 16 0 Q 4 12 16 24 Q 28 36 16 48 Q 4 56 16 60" stroke="#A1A1AA" strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M 10 54 L 16 62 L 22 54" stroke="#A1A1AA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </div>}
    </div>;
}